import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { parseFile, autoMap, applyMapping, sampleValue } from "@/lib/io";
import Modal from "./Modal";
import {
  CheckCircle2, AlertCircle, UploadCloud, FileSpreadsheet, AlertTriangle,
} from "lucide-react";

/**
 * Standardised import flow:
 *   step "upload"   — drag/drop or pick a CSV/XLSX file
 *   step "map"      — column mapping + duplicate strategy + sample preview
 *   step "running"  — progress bar
 *   step "done"     — summary (created / updated / skipped / errors)
 *
 * Props:
 *   entity     "companies" | "inventory" | "contacts"
 *   onClose    () => void
 *   onImported () => void  (called after successful run; parent reloads list)
 */
export default function ImportModal({ entity, onClose, onImported }) {
  const toast = useToast();
  const [step, setStep] = useState("upload");
  const [schema, setSchema] = useState(null);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);   // { headers, rows }
  const [mapping, setMapping] = useState({});   // sourceHeader -> targetField
  const [strategy, setStrategy] = useState("skip"); // skip | update | create
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  // Load schema
  useEffect(() => {
    api.get(`/import/${entity}/schema`).then(({ data }) => setSchema(data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [entity]);

  const targetFields = schema?.fields || [];
  const requiredFields = schema?.required || [];

  const onPickFile = async (f) => {
    setErr("");
    setFile(f);
    try {
      const out = await parseFile(f);
      if (!out.rows.length) {
        setErr("File has no data rows."); return;
      }
      setParsed(out);
      setMapping(autoMap(out.headers, targetFields));
      setStep("map");
    } catch (e) {
      setErr(`Could not parse file: ${e.message || e}`);
    }
  };

  const mappedTargets = useMemo(() =>
    new Set(Object.values(mapping).filter(Boolean)), [mapping]);

  const counts = useMemo(() => {
    const mappedCount = parsed?.headers?.filter((h) => mapping[h]).length || 0;
    const unmapped = (parsed?.headers?.length || 0) - mappedCount;
    return { mapped: mappedCount, unmapped };
  }, [parsed, mapping]);

  const missingRequired = requiredFields.filter((f) => !mappedTargets.has(f));
  const canImport = parsed && parsed.rows.length > 0 && missingRequired.length === 0;

  const runImport = async () => {
    setRunning(true); setStep("running"); setErr("");
    try {
      const payloadRows = applyMapping(parsed.rows, mapping);
      const { data } = await api.post(`/import/${entity}`, {
        rows: payloadRows,
        duplicate_strategy: strategy,
      });
      setResult(data);
      setStep("done");
      toast.success(`Import complete — created ${data.created}, updated ${data.updated}, skipped ${data.skipped}.`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setStep("map");
    } finally {
      setRunning(false);
    }
  };

  const onFinish = () => {
    onImported?.();
  };

  // ── Drag + drop handlers ──────────────────────────────────────────────
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onPickFile(f);
  };

  const stepTitle = {
    upload: `Import ${entity}`,
    map: "Map Spreadsheet Columns",
    running: "Importing…",
    done: "Import Complete",
  }[step];

  return (
    <Modal title={stepTitle} onClose={running ? undefined : onClose} size="2xl">
      {step === "upload" && (
        <div className="px-6 py-6 space-y-4">
          <p className="text-sm text-zinc-600">
            Upload a <strong>CSV</strong> or <strong>Excel (.xlsx)</strong> file. You{"\u2019"}ll be able to map columns before importing.
          </p>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={[
              "border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors",
              dragOver ? "border-brand-500 bg-brand-50/60" : "border-zinc-300 hover:border-brand-400 hover:bg-zinc-50",
            ].join(" ")}
            data-testid="import-dropzone"
          >
            <UploadCloud className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
            <p className="text-sm text-zinc-700 font-medium">Drop file here, or click to browse</p>
            <p className="text-xs text-zinc-500 mt-1">CSV / XLSX up to 5 MB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => e.target.files?.[0] && onPickFile(e.target.files[0])}
            />
          </div>
          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
        </div>
      )}

      {step === "map" && parsed && (
        <div className="px-6 py-5 space-y-5">
          {/* counters */}
          <div className="bg-zinc-50 border border-zinc-100 rounded-xl px-5 py-3 flex items-center gap-6 text-sm">
            <span className="flex items-center gap-1.5 text-green-700 font-semibold">
              <CheckCircle2 className="w-4 h-4" /> {counts.mapped} mapped
            </span>
            <span className="flex items-center gap-1.5 text-amber-600 font-semibold">
              <AlertCircle className="w-4 h-4" /> {counts.unmapped} unmapped
            </span>
            <span className="ml-auto text-xs text-zinc-500 flex items-center gap-1.5">
              <FileSpreadsheet className="w-3.5 h-3.5" />{file?.name} · {parsed.rows.length} rows
            </span>
          </div>

          {missingRequired.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 text-amber-900 text-sm rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>Required field{missingRequired.length > 1 ? "s" : ""} still unmapped: <strong>{missingRequired.join(", ")}</strong></div>
            </div>
          )}

          {/* Duplicate strategy */}
          <div>
            <div className="text-sm font-semibold text-zinc-800 mb-2">When a record already exists</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { key: "skip",   label: "Skip duplicates",  desc: "Keep existing record" },
                { key: "update", label: "Update existing", desc: "Merge new values" },
                { key: "create", label: "Create as new",   desc: "Always insert a new row" },
              ].map((opt) => (
                <label
                  key={opt.key}
                  data-testid={`import-strategy-${opt.key}`}
                  className={[
                    "border rounded-lg p-3 cursor-pointer transition-colors",
                    strategy === opt.key ? "border-brand-500 bg-brand-50/40" : "border-zinc-200 hover:border-zinc-300",
                  ].join(" ")}
                >
                  <input
                    type="radio"
                    name="dup-strategy"
                    value={opt.key}
                    checked={strategy === opt.key}
                    onChange={() => setStrategy(opt.key)}
                    className="sr-only"
                  />
                  <div className="text-sm font-semibold text-zinc-900">{opt.label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{opt.desc}</div>
                </label>
              ))}
            </div>
          </div>

          {/* Mapping list */}
          <div>
            <div className="text-sm font-semibold text-zinc-800 mb-1">Column Mappings</div>
            <p className="text-xs text-zinc-500 mb-3">
              Check the &quot;Sample&quot; value — if it shows <span className="text-red-600 font-semibold">⚠ EMPTY</span> the data is empty in your spreadsheet for that column.
            </p>
            <div className="space-y-2">
              {parsed.headers.map((h) => {
                const isReq = requiredFields.includes(mapping[h]);
                const sample = sampleValue(parsed.rows, h);
                return (
                  <div
                    key={h}
                    data-testid={`import-map-row-${h}`}
                    className="border border-zinc-200 rounded-lg px-4 py-3 flex items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 truncate">
                        {requiredFields.includes(mapping[h]) && <span className="text-brand-600">*</span>}{h}
                      </div>
                      <div className="text-xs mt-0.5 truncate">
                        <span className="text-zinc-500">Sample: </span>
                        {sample ? (
                          <span className="text-zinc-700 font-mono">{sample}</span>
                        ) : (
                          <span className="text-red-600 font-semibold">⚠ EMPTY</span>
                        )}
                      </div>
                    </div>
                    <select
                      data-testid={`import-map-select-${h}`}
                      value={mapping[h] || ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value || undefined }))}
                      className={[
                        "es-select w-56 flex-shrink-0",
                        isReq ? "border-brand-500" : "",
                      ].join(" ")}
                    >
                      <option value="">— ignore —</option>
                      {targetFields.map((f) => (
                        <option key={f} value={f}>
                          {requiredFields.includes(f) ? `* ${f}` : f}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

          <div className="flex justify-between gap-3 pt-2 sticky bottom-0 bg-white pb-1">
            <button onClick={() => setStep("upload")} className="es-btn-secondary">Back</button>
            <div className="flex gap-3">
              <button onClick={onClose} className="es-btn-secondary">Cancel</button>
              <button
                data-testid="import-run-button"
                disabled={!canImport}
                onClick={runImport}
                className="es-btn-primary"
              >
                Import {parsed.rows.length} record{parsed.rows.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "running" && (
        <div className="px-6 py-10 space-y-4 text-center">
          <UploadCloud className="w-12 h-12 text-brand-600 mx-auto animate-pulse" />
          <h4 className="font-display font-semibold text-zinc-900">Importing {parsed?.rows.length} records…</h4>
          <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full bg-brand-500 animate-pulse" style={{ width: "60%" }} />
          </div>
          <p className="text-xs text-zinc-500">This may take a few seconds.</p>
        </div>
      )}

      {step === "done" && result && (
        <div className="px-6 py-6 space-y-4">
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <div className="font-semibold text-green-900">Import finished</div>
              <div className="text-sm text-green-800/80">{result.total} row{result.total === 1 ? "" : "s"} processed.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Created" value={result.created} tone="green" />
            <Stat label="Updated" value={result.updated} tone="blue" />
            <Stat label="Skipped" value={result.skipped} tone="zinc" />
            <Stat label="Errors"  value={result.errors?.length || 0} tone="red" />
          </div>
          {result.errors?.length > 0 && (
            <div className="border border-red-200 bg-red-50/40 rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="text-sm font-semibold text-red-800 mb-2">Errors</div>
              <ul className="text-xs text-red-700 space-y-1">
                {result.errors.slice(0, 50).map((e, i) => (
                  <li key={i}>Row {(e.row_index ?? 0) + 1}: {e.message}</li>
                ))}
                {result.errors.length > 50 && <li>… and {result.errors.length - 50} more.</li>}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onFinish} data-testid="import-finish-button" className="es-btn-primary">Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    green: "bg-green-50 text-green-700 border-green-200",
    blue:  "bg-blue-50 text-blue-700 border-blue-200",
    zinc:  "bg-zinc-50 text-zinc-700 border-zinc-200",
    red:   "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`border rounded-lg px-3 py-2 ${tones[tone]}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );
}
